import { Icon } from '@design/components/icon/Icon';
import { SvgIconNames } from '@design/design-tokens/iconography/SvgIcons';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import React, { ReactNode } from 'react';

import styles from './DropdownGroup.module.scss';

export type DropdownEntry = {
  label: ReactNode;
  key: string;
  iconName?: SvgIconNames;
  disabled?: boolean;
  onClick?: (e?: Event | React.MouseEvent) => void;
};

export type DropdownGroupProps = {
  items: DropdownEntry[];
  className?: string;
};

export const DropdownGroup = ({ items, className }: DropdownGroupProps) => {
  const isMouseClickRef = React.useRef(false);

  return (
    <div
      data-testid="dropdown-wrapper"
      className={clsx(styles.dropdownWrapper, className)}
    >
      <RadixDropdown.Group className={styles.dropdownGroup}>
        {items.map(item => (
          <RadixDropdown.Item
            key={item.key}
            className={styles.dropdownMenuItem}
            // Only handle keyboard navigation - mouse clicks are handled by onClick
            onSelect={() => {
              if (!isMouseClickRef.current) {
                item?.onClick?.();
              }

              isMouseClickRef.current = false;
            }}
            disabled={item.disabled}
            data-testid={`dropdown-group-item-${item.key}`}
            data-dropdown-menu-item
            data-key={item.key}
          >
            <div
              className={clsx(styles.dropdownItemButton, {
                [styles.disabled]: item.disabled,
              })}
              // Handle mouse clicks with full MouseEvent
              onClick={e => {
                isMouseClickRef.current = true;

                if (!item.disabled) {
                  item?.onClick?.(e);
                }
              }}
            >
              {item.iconName && (
                <Icon
                  className={styles.dropdownIcon}
                  iconName={item.iconName}
                  size={16}
                />
              )}
              <span className={styles.dropdownItemLabel}>{item.label}</span>
            </div>
          </RadixDropdown.Item>
        ))}
      </RadixDropdown.Group>
      <RadixDropdown.Separator data-separator className={styles.separator} />
    </div>
  );
};
